import { getChannelClient, getChannelHost } from './singleton'
import { closeDialog } from 'src/widgets/embed/dialog'

// Opener ↔ modal channel for cross-frame interactive modals.
//
// When openModal renders a modal at the *host page* (modes 2 + 3
// crossover when the channel handshake succeeds, in the embed-script
// or FLP context), the modal iframe is a child of the host page, not
// of the donate widget that triggered it. So a modal iframe that
// needs to communicate confirmations back to its opening widget
// (gift-aid, future crypto picker, future React-mount modals) can't
// reach the opener directly — they're sibling iframes. This module
// routes through the common host:
//
//   1. Modal iframe calls sendToOpener(type, payload) — sends a
//      'toOpener' wrapper envelope over its own channel client.
//   2. The host frame's installModalForwarder catches the wrapper,
//      special-cases 'close' (closes the dialog), and otherwise
//      looks up the opener iframe in the registry and host.send's
//      the inner message down to that iframe.
//   3. The opener's onModalMessage subscriber fires.
//
// In mode 1 (top-frame donate widget) and mode 3 (channel-failed
// fallback), openModal's renderLocal puts the modal iframe inside
// the donate widget itself — so the donate widget is both opener
// AND host. The same forwarder runs there; 'close' still closes the
// dialog, and the donate widget's local onModalMessage subscribers
// catch inner messages via host.onAll directly. registerModal
// records the dialog so the forwarder can find it on 'close';
// `opener` may be null when the opener IS the local window (no
// separate iframe to forward to).

const openerByModalIframe = new WeakMap()
const dialogByModalIframe = new WeakMap()

// Called by the frame that renders a modal (via renderModal). Records
// the dialog + iframe + opener-iframe relationship so the forwarder
// can close the dialog and route messages back to the right opener.
// `opener` is the iframe that triggered the open (mode 2), or null
// when the opener is the same window that rendered the modal (mode
// 1/3 — local subscribers pick up messages via host.onAll directly,
// no host.send forwarding needed).
export const registerModal = ({ dialog, iframe, opener = null }) => {
  dialogByModalIframe.set(iframe, dialog)
  if (opener) openerByModalIframe.set(iframe, opener)
}

// Install the forwarder on a channel host. Call once per frame at
// the same place you install the openModal listener — Widgets.js#init
// for the host page, the donate widget's setup for mode 1/3.
//
// Returns a teardown for hot-restart parity with host.onAll's
// contract; production callers can ignore.
export const installModalForwarder = host =>
  host.onAll('toOpener', ({ type, payload }, _meta, fromIframe) => {
    if (type === 'close') {
      const dialog = dialogByModalIframe.get(fromIframe)
      if (dialog) closeDialog(dialog)
      return
    }
    const opener = openerByModalIframe.get(fromIframe)
    if (opener) host.send(opener, type, payload)
    // No opener registered → mode 1/3, local opener. Inner message
    // is picked up by the opener's onModalMessage subscriber via the
    // same host's onAll('toOpener', …) — the subscriber's filter on
    // the inner type fires alongside this forwarder. Nothing else
    // to do here.
  })

// Subscribe to messages from any modal opened by this window. The
// callback fires with the modal's `payload` for each matching inner
// `type`. Listens on two paths so the same subscription works
// regardless of which mode the open took:
//
//   - host.onAll: modal is a child of this window (mode 1/3 —
//     openModal renderLocal placed it here).
//   - client.onAll: modal lives at the host page; the host's
//     forwarder relayed the inner message down via host.send.
//
// Returns a teardown that unwires both listeners; safe to call on
// React effect cleanup.
export const onModalMessage = (type, handler) => {
  const host = getChannelHost({ origin: location.origin })
  const offHost = host.onAll('toOpener', ({ type: inner, payload }) => {
    if (inner === type) handler(payload)
  })

  // parent === self → no parent client to subscribe through; mode
  // 1/3 only — the host path above is enough.
  const offClient = parent !== self
    ? getChannelClient().onAll(type, handler)
    : null

  return () => {
    offHost()
    offClient?.()
  }
}

// Modal-iframe-side: send a message back to whichever opener
// triggered this modal. `type` is the consumer-facing message name
// (e.g. 'giftAidConfirmed'); the reserved 'close' asks the host to
// close the dialog without forwarding to the opener.
export const sendToOpener = (type, payload) =>
  getChannelClient().send('toOpener', { type, payload })

